<template>
  <div class="p-6">
    <div v-if="section.tab_contents?.['resources']" class="prose max-w-none">
      <div v-html="renderedContent"></div>
    </div>

    <div v-else class="text-center py-12" style="color: var(--text-muted);">
      <i class="pi pi-book text-4xl mb-4"></i>
      <p>Resources and references not available</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useMarkdown } from '@/composables/useMarkdown';
import type { Section } from '@/types/ashmatics';

const props = defineProps<{
  section: Section;
}>();

const { renderMarkdown } = useMarkdown();

const renderedContent = computed(() => {
  const content = props.section.tab_contents?.['resources'];
  return content ? renderMarkdown(content) : '';
});
</script>

<style scoped>
.prose {
  line-height: 1.7;
}

.prose :deep(h1) {
  font-size: 1.875rem;
  font-weight: 700;
  margin-top: 2rem;
  margin-bottom: 1rem;
  color: var(--text-primary);
}

.prose :deep(h2) {
  font-size: 1.5rem;
  font-weight: 600;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
  color: var(--text-primary);
}

.prose :deep(h3) {
  font-size: 1.25rem;
  font-weight: 600;
  margin-top: 1.25rem;
  margin-bottom: 0.5rem;
  color: var(--text-secondary);
}

.prose :deep(p) {
  margin-bottom: 1rem;
  color: var(--text-primary);
}

.prose :deep(ul), .prose :deep(ol) {
  margin-left: 1.5rem;
  margin-bottom: 1rem;
}

.prose :deep(li) {
  margin-bottom: 0.5rem;
  color: var(--text-primary);
}

.prose :deep(code) {
  background: var(--background-secondary);
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  color: var(--primary-gold);
}

.prose :deep(pre) {
  background: var(--background-secondary);
  padding: 1rem;
  border-radius: 0.5rem;
  overflow-x: auto;
  margin-bottom: 1rem;
}
</style>
